import {
  offerLineItems,
  offerPriceBoxes,
  offers,
  opportunityTypeEnum,
} from '@markku/db'
import {
  addVat,
  formatCurrency,
  formatCurrencyPrecise,
  formatCurrencyWithoutVat,
  MARKKU_SERVICE_CATEGORIES,
  MAINTENANCE_CATEGORY_LABELS,
  type MarkkuServiceCategory,
  VAT_PERCENTAGE,
} from '@markku/utils'
import { createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// ────────────────────────────────────────────────────────────
// Base DB-generated schemas
// ────────────────────────────────────────────────────────────

export const selectOfferSchema = createSelectSchema(offers)
export const selectOfferPriceBoxSchema = createSelectSchema(offerPriceBoxes)
export const selectOfferLineItemSchema = createSelectSchema(offerLineItems)

export type OfferRow = z.infer<typeof selectOfferSchema>
export type OfferPriceBoxRow = z.infer<typeof selectOfferPriceBoxSchema>
export type OfferLineItemRow = z.infer<typeof selectOfferLineItemSchema>

// ────────────────────────────────────────────────────────────
// JSONB block payloads (mirror of packages/db/src/schema/offer.ts)
// ────────────────────────────────────────────────────────────

export const offerOfferWorkContentItemSchema = z.object({
  id: z.string(),
  catalogRef: z
    .object({
      type: z.enum(['maintenance_task', 'product']),
      id: z.number(),
    })
    .optional(),
  title: z.string(),
  items: z.array(z.string()),
})

export const offerTrustItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
})

export const offerReferenceItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  location: z.string(),
  description: z.string(),
})

const blockBase = { id: z.string(), title: z.string() }

export const offerOptionalBlockSchema = z.discriminatedUnion('type', [
  z.object({
    ...blockBase,
    type: z.literal('trust'),
    items: z.array(offerTrustItemSchema),
  }),
  z.object({
    ...blockBase,
    type: z.literal('references'),
    items: z.array(offerReferenceItemSchema),
  }),
  z.object({
    ...blockBase,
    type: z.literal('klarna'),
    bodyText: z.string(),
  }),
  z.object({
    ...blockBase,
    type: z.literal('kotitalousvahennys'),
    bodyText: z.string(),
    // When true, render only the title + bodyText (information-only mode);
    // hide the labor/deduction/total breakdown. Use when the labor split
    // isn't reliable or the seller wants the block to be purely educational.
    hideBreakdown: z.boolean().optional(),
  }),
  z.object({
    ...blockBase,
    type: z.literal('custom'),
    bodyText: z.string(),
  }),
])

export type OfferWorkContentItem = z.infer<
  typeof offerOfferWorkContentItemSchema
>
export type OfferTrustItem = z.infer<typeof offerTrustItemSchema>
export type OfferReferenceItem = z.infer<typeof offerReferenceItemSchema>
export type OfferOptionalBlock = z.infer<typeof offerOptionalBlockSchema>
export type OfferOptionalBlockType = OfferOptionalBlock['type']

// ────────────────────────────────────────────────────────────
// Catalog sales-content snapshot + merge (shared backend ↔ ERP)
// ────────────────────────────────────────────────────────────

export interface CatalogSalesContent {
  title: string
  includedItems: string[]
  prerequisites: string[]
  exclusions: string[]
}

// Merge a catalog item's sales content into an offer draft. WorkContent is
// deduped by catalogRef (already-present catalog item is left untouched);
// edellytykset/eiSisally are deduped by string. Pure function — same logic
// runs server-side (AI suggestion) and client-side (manual catalog pick).
export function mergeSalesContent(
  existing: {
    workContent: OfferWorkContentItem[]
    edellytykset: string[]
    eiSisally: string[]
  },
  catalogRef: { type: 'maintenance_task' | 'product'; id: number },
  content: CatalogSalesContent,
): {
  workContent: OfferWorkContentItem[]
  edellytykset: string[]
  eiSisally: string[]
} {
  const dedup = (arr: string[]): string[] => Array.from(new Set(arr))

  const alreadyHasWork = existing.workContent.some(
    (w) =>
      w.catalogRef?.type === catalogRef.type &&
      w.catalogRef?.id === catalogRef.id,
  )

  const workContent = alreadyHasWork
    ? existing.workContent
    : [
        ...existing.workContent,
        {
          id: `wc-${catalogRef.type}-${catalogRef.id}-${Date.now()}`,
          catalogRef,
          title: content.title,
          items: content.includedItems,
        },
      ]

  return {
    workContent,
    edellytykset: dedup([...existing.edellytykset, ...content.prerequisites]),
    eiSisally: dedup([...existing.eiSisally, ...content.exclusions]),
  }
}

// Offer's primaryCategory is constrained to the Markku service subset
// (drives AI selection, block resolution, and the editor's category Select).
export const offerPrimaryCategorySchema = z.enum(MARKKU_SERVICE_CATEGORIES)
export type OfferPrimaryCategory = MarkkuServiceCategory

export const OFFER_PRIMARY_CATEGORIES = MARKKU_SERVICE_CATEGORIES

export const OFFER_PRIMARY_CATEGORY_LABELS: Record<
  OfferPrimaryCategory,
  string
> = Object.fromEntries(
  MARKKU_SERVICE_CATEGORIES.map((c) => [c, MAINTENANCE_CATEGORY_LABELS[c]]),
) as Record<OfferPrimaryCategory, string>

// Derived from opportunity.type at load time (offerService.loadOffer).
export const offerCustomerTypeSchema = z.enum([
  'pientalo',
  'taloyhtio',
  'yritys',
])
export type OfferCustomerType = z.infer<typeof offerCustomerTypeSchema>

// Keyed on the closed enum so a new opportunity type fails type-check
// instead of silently rendering as a consumer offer.
export const CUSTOMER_TYPE_BY_OPPORTUNITY_TYPE: Record<
  (typeof opportunityTypeEnum.enumValues)[number],
  OfferCustomerType
> = {
  household: 'pientalo',
  housing_company: 'taloyhtio',
  company: 'yritys',
  agency: 'yritys',
}

export const OFFER_VAT_INCL_LABEL = `sis. alv ${VAT_PERCENTAGE.toString().replace('.', ',')} %`
export const OFFER_VAT_0_LABEL = 'alv 0 %'

// Stored prices are alv 0 %. Pientalo sees whole euros sis. alv. B2B sees
// both figures to the cent: taloyhtiö (rarely VAT-registered, pays the VAT)
// leads with the VAT-inclusive one, yritys with alv 0 %.
export const offerPriceLines = (
  net: number,
  customerType: OfferCustomerType,
): { headline: string; vatLabel: string; secondary?: string } => {
  if (customerType === 'pientalo') {
    return {
      headline: formatCurrency(addVat(net)),
      vatLabel: OFFER_VAT_INCL_LABEL,
    }
  }
  const { withVat, withoutVat } = formatCurrencyWithoutVat(net)
  return customerType === 'taloyhtio'
    ? {
        headline: withVat,
        vatLabel: OFFER_VAT_INCL_LABEL,
        secondary: `${withoutVat} ${OFFER_VAT_0_LABEL}`,
      }
    : {
        headline: withoutVat,
        vatLabel: OFFER_VAT_0_LABEL,
        secondary: `${withVat} ${OFFER_VAT_INCL_LABEL}`,
      }
}

// Breakdown rows follow the headline: whole euros sis. alv for pientalo,
// alv 0 % to the cent for B2B so unit price × quantity reconciles.
export const offerLineAmount = (
  net: number,
  customerType: OfferCustomerType,
): string =>
  customerType === 'pientalo'
    ? formatCurrency(addVat(net))
    : formatCurrencyPrecise(net)

// ────────────────────────────────────────────────────────────
// Composite "full offer" shape — what the editor reads/writes
// ────────────────────────────────────────────────────────────

// Line item input (for save) — id optional since new rows don't have one yet
export const offerLineItemInputSchema = z.object({
  id: z.string().optional(), // client-side temp id or existing db id as string
  source: z.enum(['catalog', 'custom']),
  catalogType: z.enum(['maintenance_task', 'product']).nullable().optional(),
  catalogId: z.string().nullable().optional(),
  title: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitPrice: z.number(),
  isLabor: z.boolean(),
  // AI review tracking — aiGenerated rows start unreviewed and turn
  // reviewed when the seller either confirms or edits them.
  aiGenerated: z.boolean().optional(),
  aiReviewedAt: z.string().nullable().optional(),
})

export const offerPriceBoxInputSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  extraText: z.string().nullable().optional(),
  highlight: z.boolean(),
  lineItems: z.array(offerLineItemInputSchema),
})

export const offerCustomerSchema = z.object({
  name: z.string(),
  address: z.string(),
  contactPerson: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
})

// Full offer payload for save/load — uses string IDs on the client side
// and scalar numbers for money (backend converts to/from decimal strings)
export const offerFullSchema = z.object({
  id: z.string(),
  number: z.string(),
  status: z.enum([
    'draft',
    'sent',
    'accepted',
    'expired',
    'rejected',
    'cancelled',
  ]),
  customerType: offerCustomerTypeSchema,
  primaryCategory: offerPrimaryCategorySchema.nullable(),
  opportunityId: z.number(),
  customer: offerCustomerSchema,
  serviceDescription: z.string(),
  summary: z.string(),
  lahtotilanne: z.string().nullable().optional(),
  aikataulutus: z.string(),
  laskutus: z.string(),
  priceBoxes: z.array(offerPriceBoxInputSchema),
  workContent: z.array(offerOfferWorkContentItemSchema),
  optionalBlocks: z.array(offerOptionalBlockSchema),
  edellytykset: z.array(z.string()),
  eiSisally: z.array(z.string()),
  validForDays: z.number().int().min(1).max(365),
  validUntil: z.string().nullable(), // ISO date string — locked at send time
  responsibleUser: z
    .object({
      id: z.number().nullable(),
      name: z.string(),
      phone: z.string(),
      email: z.string(),
    })
    .nullable(),
  events: z.array(
    z.object({
      id: z.string(),
      type: z.enum([
        'created',
        'edited',
        'regenerated',
        'sent',
        'opened',
        'accepted',
        'rejected',
        'expired',
        'cancelled',
      ]),
      userId: z.number().nullable(),
      user: z.object({ id: z.number(), name: z.string() }).nullable(),
      metadata: z.record(z.unknown()),
      createdAt: z.string(),
    }),
  ),
  // Derived from the events log; exposed for backward compat (UI
  // timeline, public page state branching, accepted email template).
  tracking: z.object({
    sentAt: z.string().nullable().optional(),
    openedAt: z.string().nullable().optional(),
    acceptedAt: z.string().nullable().optional(),
    acceptedBy: z.string().nullable().optional(),
    acceptedNotes: z.string().nullable().optional(),
    cancelledAt: z.string().nullable().optional(),
    cancelledBy: z.string().nullable().optional(),
    cancelledReason: z.string().nullable().optional(),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
})

// Save input — the editor sends the full offer back
export const offerSaveInputSchema = z.object({
  id: z.number(),
  data: offerFullSchema
    .omit({
      id: true,
      number: true,
      createdAt: true,
      updatedAt: true,
      events: true,
      tracking: true,
      status: true,
      // Customer (contact + address) is owned by the opportunity, not
      // the offer. customerType is derived from opportunity.type at
      // load time. The editor never writes either through saveOffer.
      customer: true,
      customerType: true,
    })
    // validUntil is optional on save: drafts omit it (null until markSent),
    // sent offers include it so the seller can adjust the expiry through
    // the editor's autosave path.
    .extend({ validUntil: z.string().nullable().optional() }),
})

export type OfferFull = z.infer<typeof offerFullSchema>
export type OfferLineItemInput = z.infer<typeof offerLineItemInputSchema>
export type OfferPriceBoxInput = z.infer<typeof offerPriceBoxInputSchema>
export type OfferSaveInput = z.infer<typeof offerSaveInputSchema>
